import { Injectable } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { UserStorage } from '@tokeet-frontend/tv3-platform'
import { FilterGroup } from '@tv3/services/utils/filters.service'
import * as R from 'ramda'
import { filter, map, switchMapTo } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import {
  FilterFormGroup,
  isSomething,
  Rental,
  selectAllRentals,
  selectRentalsLoaded,
  sortAscend,
} from '@tokeet-frontend/tv3-platform'
import { selectAllMessageTemplates } from '@tokeet-frontend/templates'
import {
  AutomationHelpers,
  AutomationViewGroup,
  AutomationTypes,
  automationStatuses,
  selectAutomationGroupsByType,
  selectAvailableAutomationChannels,
  getAvailableAutomationEvents,
  AutomationEventGroup,
  selectAllAutomationGroups,
  automationEvents,
  automationTimeEvents,
} from '@tokeet-frontend/automations'

@Injectable()
export class AutomationsFilter {
  defaultValues = {
    rentals: [],
    channels: [],
    templates: [],
    events: [],
    status: null,
  }

  filters = {
    rentals: (automations: AutomationViewGroup[], rentals: string[]) => {
      if (!isSomething(rentals)) {
        return automations
      }
      return R.filter(
        (e: AutomationViewGroup) => AutomationHelpers.matchesAutomationScope(e.rentals, rentals),
        automations
      )
    },
    channels: (automations: AutomationViewGroup[], channels: string[]) => {
      if (!isSomething(channels)) {
        return automations
      }
      return R.filter(
        (e: AutomationViewGroup) => AutomationHelpers.matchesAutomationScope(e.channels, channels),
        automations
      )
    },
    templates: (automations: AutomationViewGroup[], templates: string[]) => {
      if (!isSomething(templates) || this.type !== AutomationTypes.Message) {
        return automations
      }
      return R.filter((e: AutomationViewGroup) => R.contains(e.template, templates), automations)
    },
    events: (automations: AutomationViewGroup[], events: string[]) => {
      if (!isSomething(events)) {
        return automations
      }
      return R.filter((e: AutomationViewGroup) => R.contains(e.event, events), automations)
    },
    status: (automations: AutomationViewGroup[], status: string) => {
      if (isSomething(status)) {
        switch (status) {
          case 'active':
            automations = R.filter((t: AutomationViewGroup) => t.status === 1 && t.archived === 0, automations)
            break
          case 'unarchived':
            automations = R.filter((t: AutomationViewGroup) => t.archived === 0, automations)
            break
          case 'archived':
            automations = R.filter((t: AutomationViewGroup) => t.archived === 1, automations)
            break
          case 'paused':
            automations = R.filter((t: AutomationViewGroup) => t.status === 0 && t.archived === 0, automations)
            break
        }
      } else {
        automations = R.filter((t: AutomationViewGroup) => t.archived === 0, automations)
      }
      return automations
    },
  }

  type: AutomationTypes | 'all'

  group: FilterFormGroup<AutomationViewGroup>

  constructor(private store: Store<any>, private fb: FormBuilder, private storage: UserStorage) {}

  setAutomationType(type: AutomationTypes | 'all') {
    this.type = type
    const isAllTypes = type === 'all'

    const storedFilters = this.storage.get(FilterGroup.Automations, this.defaultValues)

    const group = this.fb.group({
      rentals: [storedFilters.rentals],
      channels: [storedFilters.channels],
      templates: [storedFilters.templates],
      events: [storedFilters.events],
      status: [storedFilters.status],
    })

    this.group = new FilterFormGroup<AutomationViewGroup>(
      group.controls,
      this.store,
      this.storage,
      FilterGroup.Automations,
      isAllTypes ? selectAllAutomationGroups : selectAutomationGroupsByType(type),
      this.filters,
      null,
      true
    )
    this.events$ = of(
      isAllTypes ? [...automationEvents, ...automationTimeEvents] : getAvailableAutomationEvents(type, true)
    ).pipe(map((events) => sortAscend<AutomationEventGroup>('title')(events)))
  }

  rentals$ = this.store.pipe(
    select(selectRentalsLoaded),
    filter((isLoaded) => isLoaded),
    switchMapTo(this.store.pipe(select(selectAllRentals))),
    map((rentals) => {
      const chosenRentals = this.group.get('rentals').value
      const existingRentals = R.filter(
        (item) =>
          R.contains(
            item,
            R.map((r: Rental) => r.id, rentals)
          ),
        chosenRentals || []
      )
      this.group.patchValue({ rentals: existingRentals })

      return rentals
    })
  )

  channels$ = this.store.pipe(select(selectAvailableAutomationChannels))
  templates$ = this.store.pipe(select(selectAllMessageTemplates))
  events$: Observable<AutomationEventGroup[]>
  statuses$ = of(automationStatuses)
}
